"use client";

import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Database,
  Download,
  ExternalLink,
  FileText,
  GitCompare,
  History,
  Layers,
  Link2,
  Loader2,
  Merge,
  Play,
  UploadCloud,
  X,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProjectDataOverview } from "@/components/aicheck/ProjectDataOverview";
import { RunProgress } from "@/components/aicheck/RunProgress";
import { DialogShell } from "@/components/aicheck/shared/DialogShell";
import { authedFetch, readApiResponse } from "@/lib/aicheck/authed-fetch";
import {
  buildMergeCheckCacheKey,
  buildMergeStageCheckCacheKey,
  type MergeCheckResultCacheEntry,
  readMergeCheckResultCache,
} from "@/lib/aicheck/merge-check-cache";
import {
  projectDataComparisonFromSources,
  type TypedProjectDataComparison,
  typedProjectDataComparison,
} from "@/lib/aicheck/project-data-comparison";
import type { ProjectDataDiffFinding } from "@/lib/aicheck/project-data-diff";
import { projectDetailPath } from "@/lib/aicheck/routes";
import {
  buildProjectDataSourceKey,
  type ProjectDataSourceCacheCounts,
  type ProjectDataSourceCacheEntry,
  projectDataSourceSeed,
  readProjectDataSourceCache,
  writeProjectDataSourceCache,
} from "@/lib/project-data-converters/client-cache";
import {
  buildProjectDataSourceDetailsKey,
  readProjectDataSourceDetails,
  writeProjectDataSourceDetails,
} from "@/lib/project-data-converters/client-detail-store";
import type {
  AggregatedProjectData,
  MergeCheckCacheMetadata,
  MergeCheckStageKey,
  OutputLanguage,
  ProjectDataConversionUsage,
  ProjectDataSourceDescriptor,
  ProjectDataSourceKind,
  ProjectDataTraceIndex,
  ProjectDataTrustLevel,
  RunProgress as RunProgressType,
} from "@/types/aicheck";

type ConverterName = "excel" | "web-itinerary" | "manual-json";

/**
 * ia-ui Step1: 外部旅程との比較（Candidate スロット + Compare / Merge & Run）は
 * 使いどころがほぼなかったためレガシー化。UI 入口のみ隠し、エンジン・API・
 * キャッシュ互換は残置（過去の比較結果キャッシュは引き続き閲覧可能）。
 * 復活させる場合はこのフラグを true に戻す。
 */
const ENABLE_LEGACY_COMPARISON = false;

const EXTERNAL_CONVERSION_PROGRESS_THRESHOLD_BYTES = 90 * 1024;
type SlotKey = "base" | "candidate";
type SlotStatus = "idle" | "uploading" | "converting" | "ready" | "error";
type ImportIssueSeverity = "warning" | "error";
type ImportDataTab =
  | "overview"
  | "projectData"
  | "conversionReport"
  | "provenance";

interface ImportDataMetric {
  label: string;
  value: string;
}

const IMPORT_DATA_FULL_PREVIEW_LIMIT = 500 * 1024;

export interface ProjectDataRunInput {
  conversionId?: string;
  projectData?: AggregatedProjectData;
  source?: ProjectDataSourceDescriptor;
  sourceLabel: string;
  seed: string;
  comparison?: ProjectDataComparisonResult;
  mergeCheckCache?: MergeCheckCacheMetadata;
}

export type ProjectDataComparisonResult =
  TypedProjectDataComparison<ProjectDataDiffFinding>;

interface UploadApiResponse {
  ok: true;
  artifact: {
    artifactId: string;
    fileName: string;
    mimeType: string;
    size: number;
    sha256: string;
  };
}

interface ConversionApiResponse {
  ok: true;
  conversionId?: string;
  source: ProjectDataSourceDescriptor;
  projectData?: AggregatedProjectData;
  conversionReport?: Record<string, unknown>;
  provenance?: ProjectDataTraceIndex;
  usage?: ProjectDataConversionUsage;
  warnings?: string[];
  warningDetails?: string[];
  detailsKey?: string;
  hasDetails?: boolean;
  counts?: ProjectDataSourceCacheCounts;
  cached?: boolean;
  sourceKey?: string;
}

interface ConversionStreamProgress {
  phase: "waiting" | "streaming";
  tokensReceived?: number;
  bytesReceived?: number;
  contentLength?: number;
}

type ConversionStreamEvent =
  | {
      type: "progress";
      progress: ConversionStreamProgress;
    }
  | {
      type: "result";
      data: ConversionApiResponse;
    }
  | {
      type: "error";
      error?: string;
    };

interface MergeApiResponse {
  ok: true;
  baseSource: ProjectDataSourceDescriptor;
  supplementalSource: ProjectDataSourceDescriptor;
  merged: AggregatedProjectData;
  findings: ProjectDataDiffFinding[];
  summary: {
    mergedDays: number;
    mergedItems: number;
    conflicts: number;
    finalValidationFindings?: number;
    baseExisting?: number;
    candidateIntroduced?: number;
    mergeInduced?: number;
    addedFromSupplemental: number;
  };
  finalValidationFindings?: ProjectDataDiffFinding[];
  lineage?: Array<{
    mergedPath: string;
    origin: "base" | "candidate";
    sourcePath: string;
  }>;
}

interface SourceSlotState {
  sourceKind: ProjectDataSourceKind;
  trustLevel: ProjectDataTrustLevel;
  label: string;
  autoLabel: boolean;
  url: string;
  crmProjectId: string;
  file: File | null;
  conversion: ConversionApiResponse | null;
  status: SlotStatus;
  error: string | null;
  conversionStartedAt?: number;
  conversionProgress?: ConversionStreamProgress;
}

interface ImportIssueDialogState {
  severity: ImportIssueSeverity;
  title: string;
  sourceLabel: string;
  messages: string[];
}

interface ImportDataDialogState {
  title: string;
  sourceLabel: string;
  conversion: ConversionApiResponse;
}

interface SourceWorkspaceProps {
  projectId: string;
  selectedLanguage: OutputLanguage;
  activeSourceLabel?: string | null;
  restoredProjectDataInput?: ProjectDataRunInput | null;
  restoreProjectDataInputKey?: string | null;
  isRunning: boolean;
  onRunProjectData: (input: ProjectDataRunInput) => void;
  onFreeformReviewProjectData?: (input: ProjectDataRunInput) => void;
  onLoadCachedProjectData: (input: ProjectDataRunInput) => void;
  onLoadCachedMergeCheck: (entry: MergeCheckResultCacheEntry) => void;
  onComparisonResult: (result: ProjectDataComparisonResult | null) => void;
}

const SOURCE_KIND_OPTIONS: Array<{
  value: ProjectDataSourceKind;
  label: string;
}> = [
  { value: "crm-api", label: "CRM Project" },
  { value: "excel", label: "Excel" },
  { value: "pdf", label: "PDF" },
  { value: "html", label: "HTML" },
  { value: "travefy", label: "Travefy" },
  { value: "web", label: "Web URL" },
  { value: "manual-json", label: "projectData JSON" },
];

function initialSlot(slot: SlotKey): SourceSlotState {
  if (slot === "base") {
    return {
      sourceKind: "crm-api",
      trustLevel: "authoritative",
      label: "CRM base itinerary",
      autoLabel: true,
      url: "",
      crmProjectId: "",
      file: null,
      conversion: null,
      status: "idle",
      error: null,
    };
  }
  return {
    sourceKind: "pdf",
    trustLevel: "customer-facing",
    label: "Customer itinerary",
    autoLabel: true,
    url: "",
    crmProjectId: "",
    file: null,
    conversion: null,
    status: "idle",
    error: null,
  };
}

function slotTitle(slot: SlotKey): string {
  return slot === "base" ? "Base" : "Candidate";
}

function defaultSlotLabel(slot: SlotKey): string {
  return slot === "base" ? "CRM base itinerary" : "Customer itinerary";
}

function autoLabelForSlot(slot: SlotKey, state: SourceSlotState): string {
  const title = slotTitle(slot);
  if (state.file?.name) return `${title}-${state.file.name}`;
  const url = state.url?.trim();
  if (url) return `${title}-${url}`;
  const crmProjectId = state.crmProjectId?.trim();
  if (state.sourceKind === "crm-api" && crmProjectId) {
    return `${title}-CRM Project ${crmProjectId}`;
  }
  return defaultSlotLabel(slot);
}

function restoredConversionUsesAutoLabel(
  slot: SlotKey,
  conversion: ConversionApiResponse,
  projectId: string,
): boolean {
  const source = conversion.source;
  const label = source.label.trim();
  if (!label || label === defaultSlotLabel(slot)) return true;

  const title = slotTitle(slot);
  const fileName = source.origin?.fileName?.trim();
  if (fileName && (label === `${title}-${fileName}` || label === fileName)) {
    return true;
  }

  const url = source.origin?.url?.trim();
  if (url) {
    const hostLabel = (() => {
      try {
        return new URL(url).hostname;
      } catch {
        return null;
      }
    })();
    if (label === `${title}-${url}` || label === url || label === hostLabel) {
      return true;
    }
  }

  const crmProjectId =
    source.origin?.crmProjectId !== undefined
      ? String(source.origin.crmProjectId)
      : source.kind === "crm-api"
        ? projectId.trim()
        : "";
  if (
    source.kind === "crm-api" &&
    crmProjectId &&
    (label === `${title}-CRM Project ${crmProjectId}` ||
      label === `CRM Project #${crmProjectId}`)
  ) {
    return true;
  }

  return false;
}

function converterFor(sourceKind: ProjectDataSourceKind): ConverterName {
  if (sourceKind === "excel") return "excel";
  if (sourceKind === "manual-json") return "manual-json";
  return "web-itinerary";
}

function acceptsFor(sourceKind: ProjectDataSourceKind): string | undefined {
  if (sourceKind === "crm-api") return undefined;
  if (sourceKind === "excel") return ".xlsx,.xls,.xlsm";
  if (sourceKind === "pdf") return ".pdf";
  if (
    sourceKind === "html" ||
    sourceKind === "travefy" ||
    sourceKind === "web"
  ) {
    return ".html,.htm,.pdf";
  }
  if (sourceKind === "manual-json") return ".json,application/json";
  return undefined;
}

function shouldUseLLM(sourceKind: ProjectDataSourceKind): boolean {
  return sourceKind !== "crm-api" && sourceKind !== "manual-json";
}

function shouldShowExternalConversionProgress(state: SourceSlotState): boolean {
  const status = state.status ?? "idle";
  return (
    shouldUseLLM(state.sourceKind ?? "web") &&
    (status === "uploading" || status === "converting")
  );
}

function externalConversionProgressCopy(params: {
  sourceKind: ProjectDataSourceKind;
  slotTitleText: string;
  sourceLabel: string;
  isUploading: boolean;
  isStreaming: boolean;
  tokensReceived?: number;
}): { checkName: string; currentStep: string; apiStatus: string } {
  const {
    sourceKind,
    slotTitleText,
    sourceLabel,
    isUploading,
    isStreaming,
    tokensReceived,
  } = params;
  const sourceKindLabel = sourceKind.toUpperCase();
  if (sourceKind === "excel") {
    return {
      checkName: `${slotTitleText} Excel reservation matching`,
      currentStep: isUploading
        ? `Preparing ${sourceLabel} for AI reservation matching...`
        : isStreaming
          ? `Receiving Excel reservation matches for ${sourceLabel} from LLM...`
          : "Matching Excel Main rows to reservation rows with AI...",
      apiStatus: isUploading
        ? `${sourceKindLabel} source is being uploaded before LLM reservation matching.`
        : isStreaming && tokensReceived
          ? `${sourceKindLabel} conversion is receiving reservation matching output (${tokensReceived.toLocaleString()} tokens).`
          : `${sourceKindLabel} conversion is using LLM reservation matching.`,
    };
  }
  return {
    checkName: `${slotTitleText} external itinerary extraction`,
    currentStep: isUploading
      ? `Preparing ${sourceLabel} for AI extraction...`
      : isStreaming
        ? `Receiving extracted itinerary data for ${sourceLabel} from LLM...`
        : `Extracting ${sourceLabel} with AI...`,
    apiStatus: isUploading
      ? `${sourceKindLabel} source is being uploaded before LLM extraction.`
      : isStreaming && tokensReceived
        ? `${sourceKindLabel} conversion is receiving extraction output (${tokensReceived.toLocaleString()} tokens).`
        : `${sourceKindLabel} conversion is using LLM extraction.`,
  };
}

function buildExternalConversionProgress(
  slotTitleText: string,
  state: SourceSlotState,
): RunProgressType {
  const sourceKind = state.sourceKind ?? "web";
  const sourceLabel =
    state.label?.trim() ||
    state.file?.name ||
    state.url?.trim() ||
    `${slotTitleText} source`;
  const isUploading = state.status === "uploading";
  const progress = state.conversionProgress;
  const isStreaming = progress?.phase === "streaming";
  const copy = externalConversionProgressCopy({
    sourceKind,
    slotTitleText,
    sourceLabel,
    isUploading,
    isStreaming,
    tokensReceived: progress?.tokensReceived,
  });
  return {
    status: "running-ai",
    currentStep: copy.currentStep,
    completedChecks: 0,
    totalChecks: 1,
    startedAt: state.conversionStartedAt,
    plannedChecks: [copy.checkName],
    activeChecks: [
      {
        checkName: copy.checkName,
        phase: progress?.phase ?? "waiting",
        tokensReceived: progress?.tokensReceived,
        bytesReceived: progress?.bytesReceived,
        contentLength: progress?.contentLength,
      },
    ],
    apiStatus: copy.apiStatus,
  };
}

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
}

function countItems(data?: AggregatedProjectData): number {
  return (
    data?.itineraries.reduce((count, itinerary) => {
      const items = (itinerary as unknown as { itinerary_items?: unknown[] })
        .itinerary_items;
      return count + (Array.isArray(items) ? items.length : 0);
    }, 0) ?? 0
  );
}

function countConversionItems(
  conversion?: ConversionApiResponse | null,
): number {
  return (
    conversion?.counts?.itineraryItems ?? countItems(conversion?.projectData)
  );
}

function countConversionDays(
  conversion?: ConversionApiResponse | null,
): number {
  return (
    conversion?.counts?.itineraryDays ??
    conversion?.projectData?.itineraries.length ??
    0
  );
}

function countConversionArrangements(
  conversion?: ConversionApiResponse | null,
): number {
  return (
    conversion?.counts?.arrangements ??
    conversion?.projectData?.arrangements.length ??
    0
  );
}

function conversionReservationMatching(
  conversion?: ConversionApiResponse | null,
): Record<string, unknown> {
  const report = asRecord(conversion?.conversionReport);
  const sourceMap = asRecord(report.sourceMap);
  return asRecord(sourceMap.reservationMatching);
}

function countLlmReservationMatches(
  conversion?: ConversionApiResponse | null,
): number {
  return (
    finiteNumber(conversionReservationMatching(conversion).llmMatches) ?? 0
  );
}

function getConversionExtractionError(
  conversion?: ConversionApiResponse | null,
): string | null {
  if (!conversion || conversion.source.kind !== "excel") return null;
  const itineraryDays = countConversionDays(conversion);
  if (itineraryDays === 0) {
    return "Excel conversion extracted 0 itinerary days. The Main tab may not have been read correctly; check whether the itinerary layout and columns are supported.";
  }
  const itineraryItems = countConversionItems(conversion);
  if (itineraryItems === 0) {
    return "Excel conversion extracted 0 itinerary items. The Main tab may not have been read correctly; check whether the section and item columns are supported.";
  }
  return null;
}

function countsFromProjectData(
  projectData: AggregatedProjectData,
): ProjectDataSourceCacheCounts {
  return {
    itineraryDays: projectData.itineraries.length,
    itineraryItems: countItems(projectData),
    arrangements: projectData.arrangements.length,
  };
}

function conversionFromCache(
  entry: ProjectDataSourceCacheEntry,
): ConversionApiResponse {
  return {
    ok: true,
    conversionId: entry.conversionId,
    source: entry.source,
    usage: entry.usage,
    warnings: entry.warnings,
    warningDetails: entry.warningDetails,
    detailsKey: entry.detailsKey,
    hasDetails: entry.hasDetails,
    projectData: entry.projectData,
    conversionReport: entry.conversionReport,
    provenance: entry.provenance,
    counts: entry.counts,
    cached: true,
    sourceKey: entry.sourceKey,
  };
}

function conversionFromRunInput(
  input: ProjectDataRunInput,
): ConversionApiResponse | null {
  const source = input.source;
  if (!source) return null;
  return conversionFromSource(source, input.projectData, input.conversionId);
}

function conversionFromSource(
  source: ProjectDataSourceDescriptor,
  fallbackProjectData?: AggregatedProjectData,
  conversionId?: string,
): ConversionApiResponse {
  const sourceKey = source.origin?.cacheKey;
  const cached = sourceKey ? readProjectDataSourceCache(sourceKey) : null;
  if (cached) {
    const conversion = conversionFromCache(cached);
    const projectData = conversion.projectData ?? fallbackProjectData;
    return {
      ...conversion,
      source: conversion.source ?? source,
      projectData,
      counts:
        conversion.counts ??
        (projectData ? countsFromProjectData(projectData) : undefined),
    };
  }

  return {
    ok: true,
    conversionId,
    source,
    projectData: fallbackProjectData,
    conversionReport: {},
    provenance: { refsByJsonPath: {} },
    warnings: [],
    detailsKey: sourceKey
      ? buildProjectDataSourceDetailsKey(sourceKey)
      : undefined,
    hasDetails: Boolean(sourceKey),
    counts: fallbackProjectData
      ? countsFromProjectData(fallbackProjectData)
      : undefined,
    cached: true,
    sourceKey,
  };
}

function slotSourceKindFromSource(
  source: ProjectDataSourceDescriptor,
): ProjectDataSourceKind {
  return source.kind === "merged" ? "manual-json" : source.kind;
}

function slotStateFromConversion(
  slot: SlotKey,
  conversion: ConversionApiResponse,
  projectId: string,
): SourceSlotState {
  const source = conversion.source;
  return {
    sourceKind: slotSourceKindFromSource(source),
    trustLevel:
      source.trustLevel ??
      (source.kind === "crm-api" ? "authoritative" : "customer-facing"),
    label: source.label || defaultSlotLabel(slot),
    autoLabel: restoredConversionUsesAutoLabel(slot, conversion, projectId),
    url: source.origin?.url ?? "",
    crmProjectId: source.origin?.crmProjectId
      ? String(source.origin.crmProjectId)
      : source.kind === "crm-api"
        ? projectId
        : "",
    file: null,
    conversion,
    status: "ready",
    error: null,
  };
}

function mergeSummaryFromContext(
  projectData: AggregatedProjectData | undefined,
): MergeApiResponse["summary"] | null {
  const context = projectData?.mergeCheckContext;
  if (!context || !projectData) return null;
  return {
    mergedDays: projectData.itineraries.length,
    mergedItems: countItems(projectData),
    conflicts: context.summary.conflicts ?? 0,
    finalValidationFindings: context.summary.finalValidationFindings ?? 0,
    baseExisting: context.summary.baseExisting ?? 0,
    candidateIntroduced: context.summary.candidateIntroduced ?? 0,
    mergeInduced: context.summary.mergeInduced ?? 0,
    addedFromSupplemental: context.summary.addedFromSupplemental ?? 0,
  };
}

function sourceWithCacheKey(
  source: ProjectDataSourceDescriptor,
  sourceKey: string,
): ProjectDataSourceDescriptor {
  return {
    ...source,
    origin: {
      ...source.origin,
      cacheKey: sourceKey,
    },
  };
}

function mergeStageLabel(stage: MergeCheckStageKey): string {
  if (stage === "base") return "Base AI Check";
  if (stage === "candidate") return "Candidate AI Check";
  return "Final merged AI Check";
}

function stageCacheKey(
  rootMergeCacheKey: string | null,
  stage: MergeCheckStageKey,
): string | null {
  if (!rootMergeCacheKey) return null;
  return stage === "merged"
    ? rootMergeCacheKey
    : buildMergeStageCheckCacheKey(rootMergeCacheKey, stage);
}

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUsage(usage?: ProjectDataConversionUsage): string | null {
  if (!usage) return null;
  const tokens = usage.tokens as { total?: unknown } | null | undefined;
  const totalTokens =
    typeof tokens?.total === "number"
      ? `${tokens.total.toLocaleString()} tok`
      : null;
  const cost =
    typeof usage.estimatedCost === "number"
      ? `$${usage.estimatedCost.toFixed(4)}`
      : null;
  return [totalTokens, cost].filter(Boolean).join(" / ") || null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringifyIssueValue(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function describeReportItem(item: unknown, fallbackLabel: string): string {
  if (typeof item === "string") return item;
  const record = asRecord(item);
  const field = stringifyIssueValue(
    record.field ?? record.path ?? record.row ?? record.date ?? fallbackLabel,
  );
  const value = stringifyIssueValue(
    record.value ?? record.title ?? record.name ?? record.summary,
  );
  const reason = stringifyIssueValue(
    record.reason ?? record.message ?? record.note ?? record.error,
  );
  return [field, value, reason].filter(Boolean).join(" - ") || fallbackLabel;
}

const BENIGN_EXCEL_SKIPPED_ROW_REASONS = [
  "blank itinerary item row with formulas only",
  "day row is after project.travel_end_date",
];

function isBenignExcelSkippedRowReason(reason: string): boolean {
  const normalized = reason.trim().toLowerCase();
  return BENIGN_EXCEL_SKIPPED_ROW_REASONS.includes(normalized);
}

function isBenignExcelSkippedRow(item: unknown): boolean {
  const record = asRecord(item);
  const reason = stringifyIssueValue(record.reason);
  return reason ? isBenignExcelSkippedRowReason(reason) : false;
}

function isBenignConversionWarningMessage(message: string): boolean {
  const normalized = message.trim().toLowerCase();
  return (
    normalized.startsWith("skipped row:") &&
    BENIGN_EXCEL_SKIPPED_ROW_REASONS.some((reason) =>
      normalized.includes(reason),
    )
  );
}

function extractConversionWarningMessages(
  conversion?: ConversionApiResponse | null,
): string[] {
  if (!conversion) return [];

  const messages = new Set<string>();
  for (const warning of conversion.warningDetails ?? []) {
    const trimmed = warning.trim();
    if (trimmed && !isBenignConversionWarningMessage(trimmed)) {
      messages.add(trimmed);
    }
  }
  for (const warning of conversion.warnings ?? []) {
    if (warning.trim()) messages.add(warning.trim());
  }

  const report = conversion.conversionReport;
  if (report) {
    for (const item of asArray(report.skippedRows)) {
      if (isBenignExcelSkippedRow(item)) continue;
      messages.add(`Skipped row: ${describeReportItem(item, "row")}`);
    }
    for (const item of asArray(report.excludedEvents)) {
      messages.add(`Excluded event: ${describeReportItem(item, "event")}`);
    }
    const normalized = asRecord(report.normalized);
    for (const item of asArray(normalized.warnings)) {
      messages.add(
        `Normalizer warning: ${describeReportItem(item, "warning")}`,
      );
    }
    for (const item of asArray(report.inferences)) {
      messages.add(`Inferred field: ${describeReportItem(item, "inference")}`);
    }
    for (const item of asArray(report.defaults)) {
      messages.add(
        `Defaulted/generated field: ${describeReportItem(item, "default")}`,
      );
    }
  }

  return [...messages].filter(Boolean);
}

function mergeConversionDetails(
  current: ConversionApiResponse,
  details: ConversionApiResponse,
): ConversionApiResponse {
  const projectData = details.projectData ?? current.projectData;
  const detailsKey = details.detailsKey ?? current.detailsKey;
  const hasDetails =
    details.hasDetails ??
    current.hasDetails ??
    Boolean(
      details.conversionReport ||
        details.provenance ||
        details.warningDetails?.length,
    );
  return {
    ...current,
    ...details,
    source: {
      ...details.source,
      origin: {
        ...details.source.origin,
        cacheKey: current.source.origin?.cacheKey,
      },
    },
    projectData,
    counts: projectData ? countsFromProjectData(projectData) : current.counts,
    cached: current.cached,
    sourceKey: details.sourceKey ?? current.sourceKey,
    detailsKey,
    hasDetails,
  };
}

function countRecordKeysLimited(
  value: unknown,
  limit = 500,
): { label: string; sampleKeys: string[] } | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  let count = 0;
  const sampleKeys: string[] = [];
  for (const key in value as Record<string, unknown>) {
    count += 1;
    if (sampleKeys.length < 8) sampleKeys.push(key);
    if (count >= limit) {
      return { label: `${limit}+`, sampleKeys };
    }
  }
  return { label: String(count), sampleKeys };
}

function arrayLengthMetric(
  record: Record<string, unknown>,
  key: string,
  label: string,
): ImportDataMetric | null {
  const value = record[key];
  return Array.isArray(value)
    ? { label, value: value.length.toLocaleString() }
    : null;
}

function summarizeImportDataBody(
  tab: ImportDataTab,
  body: unknown,
): ImportDataMetric[] {
  if (body == null) return [{ label: "Status", value: "Not available" }];
  if (Array.isArray(body)) {
    return [
      { label: "Type", value: `Array (${body.length.toLocaleString()})` },
    ];
  }
  if (typeof body !== "object") {
    return [{ label: "Type", value: typeof body }];
  }

  const record = body as Record<string, unknown>;
  const keyCount = countRecordKeysLimited(record);
  const metrics: ImportDataMetric[] = [
    {
      label: "Top-level keys",
      value: keyCount?.label ?? "0",
    },
  ];

  if (tab === "overview" || tab === "projectData") {
    const itineraries = Array.isArray(record.itineraries)
      ? record.itineraries.length
      : null;
    const arrangements = Array.isArray(record.arrangements)
      ? record.arrangements.length
      : null;
    if (itineraries != null) {
      metrics.push({
        label: "Itinerary days",
        value: itineraries.toLocaleString(),
      });
    }
    if (arrangements != null) {
      metrics.push({
        label: "Arrangements",
        value: arrangements.toLocaleString(),
      });
    }
  }

  if (tab === "conversionReport") {
    const normalized = asRecord(record.normalized);
    const sourceMap = asRecord(record.sourceMap);
    const reservationMatching = asRecord(sourceMap.reservationMatching);
    metrics.push(
      ...[
        arrayLengthMetric(record, "skippedRows", "Skipped rows"),
        arrayLengthMetric(record, "excludedEvents", "Excluded events"),
        arrayLengthMetric(record, "inferences", "Inferences"),
        arrayLengthMetric(record, "defaults", "Defaults"),
        arrayLengthMetric(normalized, "warnings", "Normalizer warnings"),
        arrayLengthMetric(sourceMap, "itineraryItems", "Source map items"),
        arrayLengthMetric(sourceMap, "reservations", "Reservations"),
        arrayLengthMetric(sourceMap, "travefyEvents", "Travefy events"),
      ].filter((item): item is ImportDataMetric => item != null),
    );
    for (const [key, label] of [
      ["deterministicMatches", "Deterministic reservation matches"],
      ["llmMatches", "LLM reservation matches"],
      ["unmatched", "Unmatched reservations"],
      ["rejected", "Rejected LLM matches"],
    ] as const) {
      const value = finiteNumber(reservationMatching[key]);
      if (value != null) {
        metrics.push({ label, value: value.toLocaleString() });
      }
    }
  }

  if (tab === "provenance") {
    const refsByJsonPath = countRecordKeysLimited(record.refsByJsonPath);
    const refsByEntityKey = countRecordKeysLimited(record.refsByEntityKey);
    if (refsByJsonPath) {
      metrics.push({
        label: "JSON path refs",
        value: refsByJsonPath.label,
      });
    }
    if (refsByEntityKey) {
      metrics.push({
        label: "Entity refs",
        value: refsByEntityKey.label,
      });
    }
  }

  if (keyCount?.sampleKeys.length) {
    metrics.push({
      label: "Sample keys",
      value: keyCount.sampleKeys.join(", "),
    });
  }
  return metrics;
}

function previewImportDataValue(
  value: unknown,
  depth = 0,
  state: {
    seen: WeakSet<object>;
    nodes: number;
    truncated: boolean;
  } = { seen: new WeakSet<object>(), nodes: 0, truncated: false },
): unknown {
  const maxDepth = 3;
  const maxArrayItems = 12;
  const maxObjectKeys = 18;
  const maxNodes = 240;
  const maxStringLength = 500;

  if (typeof value === "string") {
    return value.length > maxStringLength
      ? `${value.slice(0, maxStringLength)}...`
      : value;
  }
  if (value == null || typeof value !== "object") return value;
  if (state.nodes >= maxNodes) {
    state.truncated = true;
    return "[Preview truncated]";
  }
  if (state.seen.has(value)) return "[Circular]";
  state.seen.add(value);
  state.nodes += 1;

  if (Array.isArray(value)) {
    if (depth >= maxDepth) return `[Array(${value.length})]`;
    const items = value
      .slice(0, maxArrayItems)
      .map((item) => previewImportDataValue(item, depth + 1, state));
    if (value.length > maxArrayItems) {
      items.push(`... ${value.length - maxArrayItems} more items`);
      state.truncated = true;
    }
    return items;
  }

  if (depth >= maxDepth) {
    const keyCount = countRecordKeysLimited(value, 100);
    return `[Object(${keyCount?.label ?? "0"} keys)]`;
  }

  const result: Record<string, unknown> = {};
  let count = 0;
  for (const key in value as Record<string, unknown>) {
    if (count >= maxObjectKeys) {
      result.__previewTruncated = "Additional keys omitted";
      state.truncated = true;
      break;
    }
    result[key] = previewImportDataValue(
      (value as Record<string, unknown>)[key],
      depth + 1,
      state,
    );
    count += 1;
  }
  return result;
}

function stringifyJsonWithinLimit(
  value: unknown,
  limit: number,
): string | null {
  const seen = new WeakSet<object>();
  const parts: string[] = [];
  let length = 0;
  let exceeded = false;

  const append = (text: string): boolean => {
    length += text.length;
    if (length > limit) {
      exceeded = true;
      return false;
    }
    parts.push(text);
    return true;
  };

  const write = (item: unknown, depth: number): boolean => {
    if (exceeded) return false;
    if (item === null) return append("null");
    if (typeof item === "string") return append(JSON.stringify(item));
    if (typeof item === "number") {
      return append(Number.isFinite(item) ? String(item) : "null");
    }
    if (typeof item === "boolean") return append(item ? "true" : "false");
    if (
      typeof item === "undefined" ||
      typeof item === "function" ||
      typeof item === "symbol"
    ) {
      return append("null");
    }
    if (typeof item !== "object") return append("null");
    if (seen.has(item)) return false;
    seen.add(item);

    const indent = "  ".repeat(depth);
    const childIndent = "  ".repeat(depth + 1);

    if (Array.isArray(item)) {
      if (!append("[")) return false;
      if (item.length > 0) {
        for (let index = 0; index < item.length; index += 1) {
          if (!append(index === 0 ? `\n${childIndent}` : `,\n${childIndent}`)) {
            return false;
          }
          const child = item[index];
          if (
            !write(
              typeof child === "undefined" ||
                typeof child === "function" ||
                typeof child === "symbol"
                ? null
                : child,
              depth + 1,
            )
          ) {
            return false;
          }
        }
        if (!append(`\n${indent}`)) return false;
      }
      return append("]");
    }

    const entries = Object.entries(item as Record<string, unknown>).filter(
      ([, child]) =>
        typeof child !== "undefined" &&
        typeof child !== "function" &&
        typeof child !== "symbol",
    );
    if (!append("{")) return false;
    if (entries.length > 0) {
      for (let index = 0; index < entries.length; index += 1) {
        const [key, child] = entries[index];
        if (!append(index === 0 ? `\n${childIndent}` : `,\n${childIndent}`)) {
          return false;
        }
        if (!append(`${JSON.stringify(key)}: `)) return false;
        if (!write(child, depth + 1)) return false;
      }
      if (!append(`\n${indent}`)) return false;
    }
    return append("}");
  };

  return write(value, 0) && !exceeded ? parts.join("") : null;
}

function stringifyImportDataPreview(value: unknown): string {
  const fullJson = stringifyJsonWithinLimit(
    value,
    IMPORT_DATA_FULL_PREVIEW_LIMIT,
  );
  if (fullJson != null) return fullJson;

  const state = { seen: new WeakSet<object>(), nodes: 0, truncated: false };
  const preview = previewImportDataValue(value, 0, state);
  const serialized = JSON.stringify(preview, null, 2) ?? "null";
  return `${serialized}\n\n[Preview truncated: full JSON is over about 500 KB. Use Open full JSON or Download JSON.]`;
}

function jsonFileName(sourceLabel: string, tab: ImportDataTab): string {
  const safeLabel =
    sourceLabel.replace(/[^\w.-]+/g, "_").slice(0, 80) || "imported_data";
  return `${safeLabel}_${tab === "overview" ? "projectData" : tab}.json`;
}

function createJsonBlobUrl(value: unknown): string {
  const blob = new Blob([`${JSON.stringify(value, null, 2)}\n`], {
    type: "application/json",
  });
  return URL.createObjectURL(blob);
}

function openJsonInNewWindow(value: unknown): void {
  const url = createJsonBlobUrl(value);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  if (!opened) throw new Error("The JSON window was blocked.");
}

function downloadJsonValue(value: unknown, fileName: string): void {
  const url = createJsonBlobUrl(value);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

async function readConversionResponse(
  response: Response,
  onProgress: (progress: ConversionStreamProgress) => void,
): Promise<ConversionApiResponse> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/x-ndjson")) {
    return readApiResponse<ConversionApiResponse>(response);
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }
  if (!response.body) {
    throw new Error("Conversion stream did not include a response body.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.trim()) continue;
        const event = JSON.parse(line) as ConversionStreamEvent;
        if (event.type === "progress") {
          onProgress(event.progress);
        } else if (event.type === "result") {
          return event.data;
        } else if (event.type === "error") {
          throw new Error(event.error ?? "Conversion failed");
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (buffer.trim()) {
    const event = JSON.parse(buffer) as ConversionStreamEvent;
    if (event.type === "progress") {
      onProgress(event.progress);
    } else if (event.type === "result") {
      return event.data;
    } else if (event.type === "error") {
      throw new Error(event.error ?? "Conversion failed");
    }
  }

  throw new Error("Conversion stream ended before returning a result.");
}

export function SourceWorkspace({
  projectId,
  selectedLanguage,
  activeSourceLabel,
  restoredProjectDataInput,
  restoreProjectDataInputKey,
  isRunning,
  onRunProjectData,
  onFreeformReviewProjectData,
  onLoadCachedProjectData,
  onLoadCachedMergeCheck,
  onComparisonResult,
}: SourceWorkspaceProps) {
  const [expanded, setExpanded] = useState(false);
  const [base, setBase] = useState<SourceSlotState>(() => initialSlot("base"));
  const [candidate, setCandidate] = useState<SourceSlotState>(() =>
    initialSlot("candidate"),
  );
  const [operationError, setOperationError] = useState<string | null>(null);
  const [comparison, setComparison] =
    useState<ProjectDataComparisonResult | null>(null);
  const [mergeSummary, setMergeSummary] = useState<
    MergeApiResponse["summary"] | null
  >(null);
  const [operationStatus, setOperationStatus] = useState<
    "idle" | "comparing" | "merging"
  >("idle");
  const [cachedMergeCheck, setCachedMergeCheck] =
    useState<MergeCheckResultCacheEntry | null>(null);
  // ia-ui Step2: 案件登録（チェックなし）
  const router = useRouter();
  const [registerBusy, setRegisterBusy] = useState(false);
  const [registerConflict, setRegisterConflict] = useState<{
    conversionId: string;
    message: string;
    candidates: Array<{
      projectKey: string;
      title: string;
      sourceLabel: string | null;
      lastRunAt: string | null;
    }>;
  } | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [cachedStageChecks, setCachedStageChecks] = useState<
    Record<MergeCheckStageKey, MergeCheckResultCacheEntry | null>
  >({
    base: null,
    candidate: null,
    merged: null,
  });
  const [importIssueDialog, setImportIssueDialog] =
    useState<ImportIssueDialogState | null>(null);
  const [importDataDialog, setImportDataDialog] =
    useState<ImportDataDialogState | null>(null);
  const lastRestoredInputKeyRef = useRef<string | null>(null);

  const convertedCount = [base.conversion, candidate.conversion].filter(
    Boolean,
  ).length;
  const crmProjectLabel =
    activeSourceLabel ?? (projectId ? `Project #${projectId}` : "No project");
  const mergeCheckCacheKey = useMemo(() => {
    if (!base.conversion || !candidate.conversion) return null;
    return buildMergeCheckCacheKey({
      baseSource: base.conversion.source,
      candidateSource: candidate.conversion.source,
      language: selectedLanguage,
    });
  }, [base.conversion, candidate.conversion, selectedLanguage]);

  const mergeStageCacheKeys = useMemo(
    () => ({
      base: stageCacheKey(mergeCheckCacheKey, "base"),
      candidate: stageCacheKey(mergeCheckCacheKey, "candidate"),
      merged: stageCacheKey(mergeCheckCacheKey, "merged"),
    }),
    [mergeCheckCacheKey],
  );

  const refreshMergeCheckCache = useCallback(() => {
    setCachedMergeCheck(readMergeCheckResultCache(mergeCheckCacheKey));
    setCachedStageChecks({
      base: readMergeCheckResultCache(mergeStageCacheKeys.base),
      candidate: readMergeCheckResultCache(mergeStageCacheKeys.candidate),
      merged: readMergeCheckResultCache(mergeStageCacheKeys.merged),
    });
  }, [mergeCheckCacheKey, mergeStageCacheKeys]);

  const openImportIssueDialog = useCallback(
    (
      severity: ImportIssueSeverity,
      title: string,
      state: SourceSlotState,
      messages: string[],
    ) => {
      setImportIssueDialog({
        severity,
        title,
        sourceLabel:
          state.conversion?.source.label ||
          state.label?.trim() ||
          defaultSlotLabel(title === "Base" ? "base" : "candidate"),
        messages: messages.length > 0 ? messages : ["No details available."],
      });
    },
    [],
  );

  const loadConversionDetails = useCallback(
    async (
      conversion: ConversionApiResponse,
    ): Promise<ConversionApiResponse> => {
      let current = conversion;
      if (
        conversion.detailsKey &&
        (!conversion.conversionReport ||
          !conversion.provenance ||
          !conversion.warningDetails?.length)
      ) {
        try {
          const details = await readProjectDataSourceDetails(
            conversion.detailsKey,
          );
          if (details) {
            current = mergeConversionDetails(conversion, {
              ok: true,
              conversionId: details.conversionId,
              source: details.source,
              conversionReport: details.conversionReport,
              provenance: details.provenance,
              warningDetails: details.warningDetails,
              detailsKey: details.detailsKey,
              hasDetails: true,
              sourceKey: details.sourceKey,
            });
          }
        } catch (error) {
          console.warn("[project-data-source-details] Failed to read", error);
        }
      }

      if (
        !current.conversionId ||
        (current.projectData && current.conversionReport)
      ) {
        return current;
      }

      try {
        const response = await authedFetch(
          `/api/aicheck/project-data/conversions/${encodeURIComponent(current.conversionId)}`,
          { method: "GET" },
        );
        const details = await readApiResponse<ConversionApiResponse>(response);
        return mergeConversionDetails(current, details);
      } catch (error) {
        if (current.projectData || current.conversionReport) return current;
        throw error;
      }
    },
    [],
  );

  const cacheConversion = useCallback((conversion: ConversionApiResponse) => {
    if (!conversion.sourceKey) return;
    const projectData = conversion.projectData;
    const counts =
      conversion.counts ??
      (projectData ? countsFromProjectData(projectData) : null);
    if (!counts) return;
    const detailsKey = buildProjectDataSourceDetailsKey(conversion.sourceKey);
    const warningDetails = extractConversionWarningMessages(conversion);
    const hasDetails = Boolean(
      conversion.conversionReport ||
        conversion.provenance ||
        warningDetails.length > 0,
    );
    writeProjectDataSourceCache({
      sourceKey: conversion.sourceKey,
      conversionId: conversion.conversionId,
      source: conversion.source,
      counts,
      usage: conversion.usage,
      warnings: conversion.warnings,
      hasDetails,
      ...(hasDetails ? { detailsKey } : {}),
      projectData: conversion.projectData,
      cachedAt: new Date().toISOString(),
    });
    if (hasDetails) {
      void writeProjectDataSourceDetails({
        detailsKey,
        sourceKey: conversion.sourceKey,
        conversionId: conversion.conversionId,
        source: conversion.source,
        conversionReport: conversion.conversionReport,
        provenance: conversion.provenance,
        warningDetails,
        cachedAt: new Date().toISOString(),
      }).catch((error) => {
        console.warn("[project-data-source-details] Failed to save", error);
      });
    }
  }, []);

  const setSlotConversion = useCallback(
    (slot: SlotKey, conversion: ConversionApiResponse) => {
      const setter = slot === "base" ? setBase : setCandidate;
      setter((current) => ({
        ...current,
        conversion,
        status: "ready",
        error: null,
        conversionStartedAt: undefined,
      }));
    },
    [],
  );

  const showImportData = useCallback(
    async (slot: SlotKey, state: SourceSlotState) => {
      if (!state.conversion) return;
      setOperationError(null);
      try {
        const conversion = await loadConversionDetails(state.conversion);
        setSlotConversion(slot, conversion);
        cacheConversion(conversion);
        setImportDataDialog({
          title: slotTitle(slot),
          sourceLabel: conversion.source.label,
          conversion,
        });
      } catch (error) {
        openImportIssueDialog("error", slotTitle(slot), state, [
          error instanceof Error
            ? error.message
            : "Imported data could not be loaded",
        ]);
      }
    },
    [
      cacheConversion,
      loadConversionDetails,
      openImportIssueDialog,
      setSlotConversion,
    ],
  );

  const hydrateCachedConversion = useCallback(
    async (
      slot: SlotKey,
      state: SourceSlotState,
      conversion: ConversionApiResponse,
      options?: { showWarnings?: boolean },
    ) => {
      let detailed = conversion;
      try {
        detailed = await loadConversionDetails(conversion);
        setSlotConversion(slot, detailed);
        cacheConversion(detailed);
      } catch {
        // Keep the cached summary usable even if the server artifact was cleaned up.
      }

      const warningMessages = extractConversionWarningMessages(detailed);
      const extractionError = getConversionExtractionError(detailed);
      if (extractionError) {
        const setter = slot === "base" ? setBase : setCandidate;
        setter((current) => ({
          ...current,
          status: "error",
          error: extractionError,
          conversionStartedAt: undefined,
          conversionProgress: undefined,
        }));
        openImportIssueDialog(
          "error",
          slotTitle(slot),
          { ...state, conversion: detailed },
          [extractionError],
        );
        return;
      }
      if (options?.showWarnings !== false && warningMessages.length > 0) {
        openImportIssueDialog(
          "warning",
          slotTitle(slot),
          { ...state, conversion: detailed },
          warningMessages,
        );
      }
    },
    [
      cacheConversion,
      loadConversionDetails,
      openImportIssueDialog,
      setSlotConversion,
    ],
  );

  const updateSlot = useCallback(
    (slot: SlotKey, patch: Partial<SourceSlotState>) => {
      const setter = slot === "base" ? setBase : setCandidate;
      setter((current) => {
        const inputChanged =
          patch.file !== undefined ||
          patch.url !== undefined ||
          patch.sourceKind !== undefined ||
          patch.crmProjectId !== undefined;
        const next: SourceSlotState = {
          ...current,
          ...patch,
          ...(patch.label !== undefined ? { autoLabel: false } : {}),
          ...(inputChanged
            ? {
                conversion: null,
                status: "idle" as const,
                error: null,
                conversionStartedAt: undefined,
              }
            : {}),
        };
        if (
          inputChanged &&
          patch.label === undefined &&
          current.autoLabel !== false
        ) {
          next.label = autoLabelForSlot(slot, next);
          next.autoLabel = true;
        }
        return next;
      });
      if (
        patch.file !== undefined ||
        patch.url !== undefined ||
        patch.sourceKind !== undefined ||
        patch.crmProjectId !== undefined
      ) {
        setComparison(null);
        setMergeSummary(null);
        setCachedMergeCheck(null);
        onComparisonResult(null);
      }
    },
    [onComparisonResult],
  );

  const uploadFile = useCallback(async (file: File) => {
    const form = new FormData();
    form.set("file", file);
    const response = await authedFetch("/api/aicheck/project-data/uploads", {
      method: "POST",
      body: form,
    });
    return readApiResponse<UploadApiResponse>(response);
  }, []);

  const buildSlotSourceKeyParams = useCallback((state: SourceSlotState) => {
    const sourceKind = state.sourceKind ?? "excel";
    return {
      sourceKind,
      file: state.file,
      url: state.url ?? "",
      crmProjectId: state.crmProjectId ?? "",
      converter: converterFor(sourceKind),
      useLLM: shouldUseLLM(sourceKind),
    };
  }, []);

  const buildSlotSourceKey = useCallback(
    (state: SourceSlotState) => {
      return buildProjectDataSourceKey(buildSlotSourceKeyParams(state));
    },
    [buildSlotSourceKeyParams],
  );

  const toRunInput = useCallback(
    (conversion: ConversionApiResponse): ProjectDataRunInput => ({
      conversionId: conversion.projectData
        ? undefined
        : conversion.conversionId,
      projectData: conversion.projectData,
      source: conversion.source,
      sourceLabel: conversion.source.label,
      seed: projectDataSourceSeed(
        conversion.source,
        conversion.conversionId || "",
      ),
    }),
    [],
  );

  const buildMergeStageRunInput = useCallback(
    (params: {
      stage: MergeCheckStageKey;
      projectData: AggregatedProjectData;
      source: ProjectDataSourceDescriptor;
    }): ProjectDataRunInput => {
      const { stage, projectData, source } = params;
      const rootMergeCacheKey = mergeCheckCacheKey;
      const stageKey = stageCacheKey(rootMergeCacheKey, stage);
      const baseSource = base.conversion?.source;
      const candidateSource = candidate.conversion?.source;
      const sourceLabel = `${mergeStageLabel(stage)}: ${source.label}`;
      const seed = [
        rootMergeCacheKey ?? "merge-check",
        stage,
        source.dataDigest,
        source.sourceId,
      ].join(":");
      const originWithoutCrm = { ...(source.origin ?? {}) };
      delete originWithoutCrm.crmProjectId;
      const stageSource: ProjectDataSourceDescriptor = {
        ...source,
        sourceId: `${source.sourceId}:${stage}`,
        label: sourceLabel,
        dataDigest: seed,
        origin: {
          ...originWithoutCrm,
          generatedAt: new Date().toISOString(),
          ...(stageKey ? { cacheKey: stageKey } : undefined),
        },
      };
      return {
        projectData,
        source: stageSource,
        sourceLabel,
        seed,
        ...(stageKey && rootMergeCacheKey && baseSource && candidateSource
          ? {
              mergeCheckCache: {
                mergeCacheKey: stageKey,
                rootMergeCacheKey,
                stage,
                sourceLabel,
                seed,
                baseSource,
                candidateSource,
              },
            }
          : {}),
      };
    },
    [base.conversion, candidate.conversion, mergeCheckCacheKey],
  );

  const applyCachedSource = useCallback(
    (slot: SlotKey, state: SourceSlotState) => {
      const sourceKey = buildSlotSourceKey(state);
      if (!sourceKey) return;

      if (state.sourceKind === "crm-api") return;

      const cached = readProjectDataSourceCache(sourceKey);
      if (!cached) return;
      if (state.conversion?.sourceKey === sourceKey) return;

      const conversion = conversionFromCache(cached);
      const extractionError = getConversionExtractionError(conversion);
      const setter = slot === "base" ? setBase : setCandidate;
      if (extractionError) {
        setter((current) => ({
          ...current,
          conversion,
          status: "error",
          error: extractionError,
          conversionStartedAt: undefined,
          conversionProgress: undefined,
        }));
        setComparison(null);
        onComparisonResult(null);
        openImportIssueDialog(
          "error",
          slotTitle(slot),
          { ...state, conversion },
          [extractionError],
        );
        return;
      }
      setter((current) => ({
        ...current,
        conversion,
        status: "ready",
        error: null,
        conversionStartedAt: undefined,
      }));
      setComparison(null);
      onComparisonResult(null);
      onLoadCachedProjectData(toRunInput(conversion));
      if (
        conversion.hasDetails ||
        conversion.detailsKey ||
        extractConversionWarningMessages(conversion).length > 0
      ) {
        void hydrateCachedConversion(slot, state, conversion, {
          showWarnings: false,
        });
      }
    },
    [
      buildSlotSourceKey,
      hydrateCachedConversion,
      onComparisonResult,
      onLoadCachedProjectData,
      openImportIssueDialog,
      toRunInput,
    ],
  );

  useEffect(() => {
    if (
      projectId &&
      !(base.crmProjectId ?? "") &&
      base.sourceKind === "crm-api"
    ) {
      setBase((current) => ({
        ...current,
        crmProjectId: current.crmProjectId || projectId,
        label:
          current.autoLabel !== false
            ? autoLabelForSlot("base", {
                ...current,
                crmProjectId: current.crmProjectId || projectId,
              })
            : current.label,
      }));
    }
  }, [base.crmProjectId, base.sourceKind, projectId]);

  useEffect(() => {
    if (!restoreProjectDataInputKey) {
      lastRestoredInputKeyRef.current = null;
      return;
    }
    if (lastRestoredInputKeyRef.current === restoreProjectDataInputKey) return;
    lastRestoredInputKeyRef.current = restoreProjectDataInputKey;

    const input = restoredProjectDataInput;
    const source = input?.source;
    if (!input || !source || source.kind === "crm-api") return;

    const mergeMetadata = input.mergeCheckCache;
    if (mergeMetadata?.baseSource && mergeMetadata.candidateSource) {
      const rootMergeCacheKey =
        mergeMetadata.rootMergeCacheKey ?? mergeMetadata.mergeCacheKey;
      const stageEntries = {
        base: readMergeCheckResultCache(
          stageCacheKey(rootMergeCacheKey, "base"),
        ),
        candidate: readMergeCheckResultCache(
          stageCacheKey(rootMergeCacheKey, "candidate"),
        ),
        merged: readMergeCheckResultCache(
          stageCacheKey(rootMergeCacheKey, "merged"),
        ),
      };
      const rootEntry = readMergeCheckResultCache(rootMergeCacheKey);
      const baseConversion = conversionFromSource(
        mergeMetadata.baseSource,
        stageEntries.base?.result.projectData,
      );
      const candidateConversion = conversionFromSource(
        mergeMetadata.candidateSource,
        stageEntries.candidate?.result.projectData,
      );
      setBase(slotStateFromConversion("base", baseConversion, projectId));
      setCandidate(
        slotStateFromConversion("candidate", candidateConversion, projectId),
      );
      setCachedMergeCheck(rootEntry);
      setCachedStageChecks(stageEntries);
      setOperationError(null);
      const restoredComparison =
        typedProjectDataComparison<ProjectDataDiffFinding>(
          projectDataComparisonFromSources(
            { comparison: input.comparison, projectData: input.projectData },
            { comparison: rootEntry?.comparison, result: rootEntry?.result },
            {
              comparison: stageEntries.merged?.comparison,
              result: stageEntries.merged?.result,
            },
          ),
        );
      setComparison(restoredComparison);
      setMergeSummary(
        mergeSummaryFromContext(
          input.projectData ??
            rootEntry?.result.projectData ??
            stageEntries.merged?.result.projectData,
        ),
      );
      onComparisonResult(restoredComparison);
      return;
    }

    const conversion = conversionFromRunInput(input);
    if (!conversion) return;

    setBase(slotStateFromConversion("base", conversion, projectId));
    setCandidate(initialSlot("candidate"));
    setComparison(input.comparison ?? null);
    setMergeSummary(null);
    setOperationError(null);
    onComparisonResult(input.comparison ?? null);
  }, [
    onComparisonResult,
    projectId,
    restoredProjectDataInput,
    restoreProjectDataInputKey,
  ]);

  useEffect(() => {
    applyCachedSource("base", base);
  }, [applyCachedSource, base]);

  useEffect(() => {
    applyCachedSource("candidate", candidate);
  }, [applyCachedSource, candidate]);

  useEffect(() => {
    refreshMergeCheckCache();
  }, [refreshMergeCheckCache]);

  useEffect(() => {
    if (!isRunning) refreshMergeCheckCache();
  }, [isRunning, refreshMergeCheckCache]);

  const convertSlot = useCallback(
    async (slot: SlotKey) => {
      const state = slot === "base" ? base : candidate;
      const setter = slot === "base" ? setBase : setCandidate;
      const sourceKind = state.sourceKind ?? "excel";
      const trustLevel =
        state.trustLevel ??
        (sourceKind === "crm-api" ? "authoritative" : "customer-facing");
      const isCrmSource = sourceKind === "crm-api";
      const crmProjectIdText = state.crmProjectId?.trim() ?? "";
      const urlText = state.url?.trim() ?? "";
      const labelText = state.label?.trim() ?? "";
      if (isCrmSource ? !crmProjectIdText : !state.file && !urlText) {
        const message = isCrmSource
          ? "Enter a CRM project ID."
          : "Select a file or enter a URL.";
        setter((current) => ({
          ...current,
          status: "error",
          error: message,
        }));
        openImportIssueDialog("error", slotTitle(slot), state, [message]);
        return;
      }

      setOperationError(null);
      setMergeSummary(null);
      const startedAt = Date.now();
      setter((current) => ({
        ...current,
        status: "uploading",
        error: null,
        conversionStartedAt: shouldUseLLM(sourceKind) ? startedAt : undefined,
        conversionProgress: undefined,
      }));
      if (shouldUseLLM(sourceKind)) {
        await waitForNextPaint();
      }

      try {
        const sourceKey = buildSlotSourceKey(state);
        if (isCrmSource) {
          const crmProjectId = Number(crmProjectIdText);
          if (!Number.isInteger(crmProjectId) || crmProjectId <= 0) {
            throw new Error("CRM project ID must be a positive integer.");
          }
          const response = await authedFetch(
            `/api/aicheck/project-data/${encodeURIComponent(String(crmProjectId))}`,
            { method: "GET" },
          );
          const projectData =
            await readApiResponse<AggregatedProjectData>(response);
          const source: ProjectDataSourceDescriptor = {
            sourceId: `crm-${crmProjectId}`,
            label: labelText || `CRM Project #${crmProjectId}`,
            kind: "crm-api",
            trustLevel: "authoritative",
            dataDigest: `crm-project-${crmProjectId}`,
            origin: {
              crmProjectId,
              generatedAt: projectData.fetchedAt,
              ...(sourceKey ? { cacheKey: sourceKey } : undefined),
            },
          };
          const conversion: ConversionApiResponse = {
            ok: true,
            source,
            projectData,
            counts: countsFromProjectData(projectData),
            sourceKey: sourceKey ?? undefined,
          };
          cacheConversion(conversion);
          setter((current) => ({
            ...current,
            conversion,
            status: "ready",
            error: null,
            conversionStartedAt: undefined,
            conversionProgress: undefined,
          }));
          setComparison(null);
          onComparisonResult(null);
          onLoadCachedProjectData(toRunInput(conversion));
          return;
        }

        const upload = state.file ? await uploadFile(state.file) : null;
        setter((current) => ({
          ...current,
          status: "converting",
          conversionStartedAt: shouldUseLLM(sourceKind) ? startedAt : undefined,
          conversionProgress: shouldUseLLM(sourceKind)
            ? {
                phase: "waiting",
                tokensReceived: 0,
                bytesReceived: 0,
                contentLength: 0,
              }
            : undefined,
        }));
        if (shouldUseLLM(sourceKind)) {
          await waitForNextPaint();
        }

        const response = await authedFetch(
          "/api/aicheck/project-data/convert",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sourceKind,
              artifactId: upload?.artifact.artifactId,
              url: urlText || undefined,
              converter: converterFor(sourceKind),
              label: labelText || undefined,
              trustLevel,
              force: true,
              streamProgress: shouldUseLLM(sourceKind),
              options: {
                useLLM: shouldUseLLM(sourceKind),
              },
            }),
          },
        );
        const conversion = await readConversionResponse(
          response,
          (progress) => {
            setter((current) => ({
              ...current,
              conversionProgress: progress,
            }));
          },
        );
        const extractionError = getConversionExtractionError(conversion);
        if (extractionError) {
          throw new Error(extractionError);
        }
        if (sourceKey && conversion.projectData) {
          conversion.source = sourceWithCacheKey(conversion.source, sourceKey);
          conversion.counts = countsFromProjectData(conversion.projectData);
          conversion.sourceKey = sourceKey;
          conversion.warningDetails =
            extractConversionWarningMessages(conversion);
          cacheConversion(conversion);
        }
        setter((current) => ({
          ...current,
          conversion,
          status: "ready",
          error: null,
          conversionStartedAt: undefined,
          conversionProgress: undefined,
        }));
        setComparison(null);
        onComparisonResult(null);
        onLoadCachedProjectData(toRunInput(conversion));
        const warningMessages = extractConversionWarningMessages(conversion);
        if (warningMessages.length > 0) {
          openImportIssueDialog(
            "warning",
            slotTitle(slot),
            { ...state, conversion },
            warningMessages,
          );
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Conversion failed";
        setter((current) => ({
          ...current,
          status: "error",
          error: message,
          conversionStartedAt: undefined,
          conversionProgress: undefined,
        }));
        openImportIssueDialog("error", slotTitle(slot), state, [message]);
      }
    },
    [
      base,
      buildSlotSourceKey,
      cacheConversion,
      candidate,
      onLoadCachedProjectData,
      onComparisonResult,
      openImportIssueDialog,
      toRunInput,
      uploadFile,
    ],
  );

  const runConversion = useCallback(
    async (slot: SlotKey, conversion: ConversionApiResponse) => {
      let detailed = conversion;
      try {
        detailed = await loadConversionDetails(conversion);
        setSlotConversion(slot, detailed);
        cacheConversion(detailed);
      } catch {
        // Fall back to the summary conversion; conversionId may still be usable.
      }

      if (
        mergeCheckCacheKey &&
        detailed.projectData &&
        (slot === "base" || slot === "candidate")
      ) {
        onRunProjectData(
          buildMergeStageRunInput({
            stage: slot,
            projectData: detailed.projectData,
            source: detailed.source,
          }),
        );
        return;
      }

      onRunProjectData(toRunInput(detailed));
    },
    [
      buildMergeStageRunInput,
      cacheConversion,
      loadConversionDetails,
      mergeCheckCacheKey,
      onRunProjectData,
      setSlotConversion,
      toRunInput,
    ],
  );

  const openFreeformReview = useCallback(
    async (slot: SlotKey, conversion: ConversionApiResponse) => {
      let detailed = conversion;
      try {
        detailed = await loadConversionDetails(conversion);
        setSlotConversion(slot, detailed);
        cacheConversion(detailed);
      } catch {
        // Fall back to the summary conversion; conversionId may still be usable.
      }
      if (!detailed.projectData) {
        openImportIssueDialog(
          "error",
          slotTitle(slot),
          {
            ...(slot === "base" ? base : candidate),
            conversion: detailed,
          },
          [
            "projectData could not be loaded. Convert again or open View data first.",
          ],
        );
        return;
      }
      onFreeformReviewProjectData?.(toRunInput(detailed));
    },
    [
      base,
      cacheConversion,
      candidate,
      loadConversionDetails,
      onFreeformReviewProjectData,
      openImportIssueDialog,
      setSlotConversion,
      toRunInput,
    ],
  );

  const buildMergedRunInput = useCallback(
    (
      merged: AggregatedProjectData,
      comparisonResult?: ProjectDataComparisonResult,
    ): ProjectDataRunInput => {
      const baseSource = base.conversion?.source;
      const candidateSource = candidate.conversion?.source;
      const baseLabel = base.conversion?.source.label ?? "Base itinerary";
      const candidateLabel =
        candidate.conversion?.source.label ?? "Customer itinerary";
      const sourceLabel = `Merged: ${baseLabel} + ${candidateLabel}`;
      const seed = `${baseSource?.dataDigest ?? ""}:${
        candidateSource?.dataDigest ?? ""
      }:merged`;
      const source: ProjectDataSourceDescriptor = {
        sourceId: mergeCheckCacheKey ?? `merged-${seed}`,
        label: sourceLabel,
        kind: "merged",
        trustLevel: "supplemental",
        dataDigest: seed,
        origin: {
          generatedAt: new Date().toISOString(),
          ...(mergeCheckCacheKey ? { cacheKey: mergeCheckCacheKey } : {}),
        },
      };
      return {
        projectData: merged,
        source,
        sourceLabel,
        seed,
        comparison: comparisonResult,
        ...(mergeCheckCacheKey && baseSource && candidateSource
          ? {
              mergeCheckCache: {
                mergeCacheKey: mergeCheckCacheKey,
                rootMergeCacheKey: mergeCheckCacheKey,
                stage: "merged",
                sourceLabel,
                seed,
                baseSource,
                candidateSource,
              },
            }
          : {}),
      };
    },
    [base.conversion, candidate.conversion, mergeCheckCacheKey],
  );

  const runMerged = useCallback(
    (
      merged: AggregatedProjectData,
      comparisonResult?: ProjectDataComparisonResult,
    ) => {
      onRunProjectData(buildMergedRunInput(merged, comparisonResult));
    },
    [buildMergedRunInput, onRunProjectData],
  );

  const sourcedPayload = useCallback((conversion: ConversionApiResponse) => {
    if (!conversion.projectData) {
      if (conversion.conversionId) {
        return { conversionId: conversion.conversionId };
      }
      throw new Error("Cached source data is unavailable. Convert again.");
    }
    return {
      data: conversion.projectData,
      source: conversion.source,
      trace: conversion.provenance,
    };
  }, []);

  const registerAsProject = useCallback(
    async (
      conversionId: string,
      mode?: "new" | "update",
      updateProjectKey?: string,
    ) => {
      setRegisterBusy(true);
      setRegisterError(null);
      try {
        const response = await authedFetch(
          "/api/aicheck/projects/register-external",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              conversionId,
              ...(mode ? { mode } : {}),
              ...(updateProjectKey ? { updateProjectKey } : {}),
            }),
          },
        );
        const data = (await response.json().catch(() => null)) as {
          ok?: boolean;
          projectKey?: string;
          conflict?: boolean;
          message?: string;
          candidates?: Array<{
            projectKey: string;
            title: string;
            sourceLabel: string | null;
            lastRunAt: string | null;
          }>;
          error?: string;
        } | null;
        if (response.status === 409 && data?.conflict) {
          setRegisterConflict({
            conversionId,
            message: data.message ?? "既存の外部案件があります。",
            candidates: data.candidates ?? [],
          });
          return;
        }
        if (!response.ok || !data?.ok || !data.projectKey) {
          throw new Error(data?.error ?? `HTTP ${response.status}`);
        }
        setRegisterConflict(null);
        router.push(projectDetailPath(data.projectKey));
      } catch (error) {
        setRegisterError(
          error instanceof Error ? error.message : "登録に失敗しました。",
        );
      } finally {
        setRegisterBusy(false);
      }
    },
    [router],
  );

  const compareSources = useCallback(async () => {
    if (!base.conversion || !candidate.conversion) return;
    setOperationError(null);
    setOperationStatus("comparing");
    try {
      const response = await authedFetch("/api/aicheck/project-data/compare", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          base: sourcedPayload(base.conversion),
          candidate: sourcedPayload(candidate.conversion),
        }),
      });
      const result =
        await readApiResponse<ProjectDataComparisonResult>(response);
      setComparison(result);
      onComparisonResult(result);
    } catch (error) {
      setOperationError(
        error instanceof Error ? error.message : "Comparison failed",
      );
    } finally {
      setOperationStatus("idle");
    }
  }, [
    base.conversion,
    candidate.conversion,
    onComparisonResult,
    sourcedPayload,
  ]);

  const mergeAndRun = useCallback(async () => {
    if (!base.conversion || !candidate.conversion) return;
    setOperationError(null);
    setOperationStatus("merging");
    try {
      const response = await authedFetch("/api/aicheck/project-data/merge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          base: sourcedPayload(base.conversion),
          supplemental: sourcedPayload(candidate.conversion),
        }),
      });
      const result = await readApiResponse<MergeApiResponse>(response);
      const mergedFindings = [
        ...result.findings,
        ...(result.finalValidationFindings ?? []),
      ];
      const comparisonResult: ProjectDataComparisonResult = {
        baseSource: result.baseSource,
        candidateSource: result.supplementalSource,
        findings: mergedFindings,
        matches: [],
        summary: {
          matches: 0,
          findings: mergedFindings.length,
          conflicts: mergedFindings.filter(
            (finding) => finding.kind === "conflict",
          ).length,
          missingInCandidate: result.findings.filter(
            (finding) => finding.kind === "missing_in_candidate",
          ).length,
          extraInCandidate: result.summary.addedFromSupplemental,
          finalValidationFindings: result.summary.finalValidationFindings ?? 0,
          baseExisting: result.summary.baseExisting ?? 0,
          candidateIntroduced: result.summary.candidateIntroduced ?? 0,
          mergeInduced: result.summary.mergeInduced ?? 0,
        },
      };
      setMergeSummary(result.summary);
      setComparison(comparisonResult);
      onComparisonResult(comparisonResult);
      runMerged(result.merged, comparisonResult);
      refreshMergeCheckCache();
    } catch (error) {
      setOperationError(
        error instanceof Error ? error.message : "Merge failed",
      );
    } finally {
      setOperationStatus("idle");
    }
  }, [
    base.conversion,
    candidate.conversion,
    onComparisonResult,
    refreshMergeCheckCache,
    runMerged,
    sourcedPayload,
  ]);

  const readyForPairActions = Boolean(base.conversion && candidate.conversion);
  const isBusy =
    isRunning ||
    operationStatus !== "idle" ||
    base.status === "uploading" ||
    base.status === "converting" ||
    candidate.status === "uploading" ||
    candidate.status === "converting";

  const statusLine = useMemo(() => {
    if (comparison) {
      return `${comparison.summary.findings} differences, ${comparison.summary.conflicts} conflicts`;
    }
    if (convertedCount > 0) {
      return `${convertedCount} converted source${convertedCount > 1 ? "s" : ""}`;
    }
    return "CRM API";
  }, [comparison, convertedCount]);
  const externalConversionProgress = shouldShowExternalConversionProgress(base)
    ? buildExternalConversionProgress("Base", base)
    : shouldShowExternalConversionProgress(candidate)
      ? buildExternalConversionProgress("Candidate", candidate)
      : null;

  if (!expanded) {
    return (
      <div className="mb-4 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
              <Database size={16} />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium text-gray-500">
                旅程取り込み
              </div>
              <div className="truncate text-sm font-semibold text-gray-900">
                {crmProjectLabel} · {statusLine}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700"
          >
            <ChevronRight size={14} />
            旅程取り込み
          </button>
        </div>
        {externalConversionProgress && (
          <div className="mt-3">
            <RunProgress
              progress={externalConversionProgress}
              progressThresholdBytes={
                EXTERNAL_CONVERSION_PROGRESS_THRESHOLD_BYTES
              }
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
            <Layers size={16} />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-500">
              旅程取り込み
            </div>
            <div className="truncate text-sm font-semibold text-gray-900">
              {crmProjectLabel} · {statusLine}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700"
        >
          <ChevronDown size={14} />
          Collapse
        </button>
      </div>

      <div
        className={`grid gap-4 p-4 ${ENABLE_LEGACY_COMPARISON ? "lg:grid-cols-2" : ""}`}
      >
        <SourceSlotPanel
          title="Base"
          state={base}
          isBusy={isBusy}
          onChange={(patch) => updateSlot("base", patch)}
          onConvert={() => void convertSlot("base")}
          onRun={() =>
            base.conversion && void runConversion("base", base.conversion)
          }
          onFreeform={() =>
            base.conversion && void openFreeformReview("base", base.conversion)
          }
          onRegister={() =>
            base.conversion?.conversionId &&
            void registerAsProject(base.conversion.conversionId)
          }
          registerBusy={registerBusy}
          onShowIssues={(severity, messages) =>
            openImportIssueDialog(severity, "Base", base, messages)
          }
          onShowData={() => void showImportData("base", base)}
        />
        {ENABLE_LEGACY_COMPARISON && (
          <SourceSlotPanel
            title="Candidate"
            state={candidate}
            isBusy={isBusy}
            onChange={(patch) => updateSlot("candidate", patch)}
            onConvert={() => void convertSlot("candidate")}
            onRun={() =>
              candidate.conversion &&
              void runConversion("candidate", candidate.conversion)
            }
            onFreeform={() =>
              candidate.conversion &&
              void openFreeformReview("candidate", candidate.conversion)
            }
            onShowIssues={(severity, messages) =>
              openImportIssueDialog(severity, "Candidate", candidate, messages)
            }
            onShowData={() => void showImportData("candidate", candidate)}
          />
        )}
      </div>

      <div className="border-t border-gray-100 px-4 py-3">
        {ENABLE_LEGACY_COMPARISON && readyForPairActions && (
          <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="mb-2 text-xs font-semibold text-gray-500">
              Staged AI Check
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  base.conversion && void runConversion("base", base.conversion)
                }
                disabled={!base.conversion || isBusy}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Play size={13} />
                Run Base AI
              </button>
              <button
                type="button"
                onClick={() =>
                  candidate.conversion &&
                  void runConversion("candidate", candidate.conversion)
                }
                disabled={!candidate.conversion || isBusy}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Play size={13} />
                Run Candidate AI
              </button>
              {(["base", "candidate", "merged"] as const).map((stage) => {
                const cached = cachedStageChecks[stage];
                if (!cached) return null;
                return (
                  <button
                    key={stage}
                    type="button"
                    onClick={() => onLoadCachedMergeCheck(cached)}
                    disabled={isBusy}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <History size={13} />
                    Show {mergeStageLabel(stage)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div
          className={`flex flex-wrap items-center gap-2 ${ENABLE_LEGACY_COMPARISON ? "" : "hidden"}`}
        >
          <button
            type="button"
            onClick={() => void compareSources()}
            disabled={!readyForPairActions || isBusy}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-gray-900 px-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {operationStatus === "comparing" ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <GitCompare size={15} />
            )}
            Compare
          </button>
          <button
            type="button"
            onClick={() => void mergeAndRun()}
            disabled={!readyForPairActions || isBusy}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {operationStatus === "merging" ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Merge size={15} />
            )}
            {cachedMergeCheck
              ? "Re-run Final AI Check"
              : "Merge & Run Final AI"}
          </button>
          {cachedMergeCheck && (
            <button
              type="button"
              onClick={() => onLoadCachedMergeCheck(cachedMergeCheck)}
              disabled={isBusy}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <History size={15} />
              Show Cached Merge Check
            </button>
          )}
          {comparison && (
            <span className="text-xs font-medium text-gray-500">
              {comparison.summary.matches} matches /{" "}
              {comparison.summary.findings} findings
            </span>
          )}
          {mergeSummary && (
            <span className="text-xs font-medium text-gray-500">
              {mergeSummary.addedFromSupplemental} added from candidate
            </span>
          )}
        </div>
        {operationError && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>{operationError}</span>
          </div>
        )}
      </div>

      {registerError && (
        <div className="mx-4 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {registerError}
        </div>
      )}
      {registerConflict && (
        <DialogShell
          onClose={() => setRegisterConflict(null)}
          backdropAriaLabel="キャンセル"
          panelClassName="relative z-10 w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-2xl"
        >
          <h4 className="text-sm font-semibold text-gray-900">
            同じ旅程 ID の外部案件があります
          </h4>
          <p className="mt-2 text-xs text-gray-600">
            {registerConflict.message}
          </p>
          <div className="mt-3 space-y-2">
            {registerConflict.candidates.map((candidate) => (
              <button
                key={candidate.projectKey}
                type="button"
                disabled={registerBusy}
                onClick={() =>
                  void registerAsProject(
                    registerConflict.conversionId,
                    "update",
                    candidate.projectKey,
                  )
                }
                className="flex w-full items-center justify-between rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-left text-sm text-indigo-800 hover:bg-indigo-100 disabled:opacity-50"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">
                    {candidate.title || candidate.projectKey}
                  </span>
                  <span className="block truncate text-[11px] text-indigo-500">
                    {candidate.sourceLabel ?? ""}
                    {candidate.lastRunAt
                      ? ` · 最終チェック ${candidate.lastRunAt.slice(0, 10)}`
                      : " · 未チェック"}
                  </span>
                </span>
                <span className="ml-3 shrink-0 text-xs font-semibold">
                  この案件の更新として取り込む
                </span>
              </button>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              disabled={registerBusy}
              onClick={() => setRegisterConflict(null)}
              className="rounded border border-gray-300 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            >
              キャンセル
            </button>
            <button
              type="button"
              disabled={registerBusy}
              onClick={() =>
                void registerAsProject(registerConflict.conversionId, "new")
              }
              className="rounded bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              新規案件として登録
            </button>
          </div>
        </DialogShell>
      )}
      <ImportIssueDialog
        issue={importIssueDialog}
        onClose={() => setImportIssueDialog(null)}
      />
      <ImportDataDialog
        data={importDataDialog}
        onClose={() => setImportDataDialog(null)}
      />
    </div>
  );
}

function SourceSlotPanel({
  title,
  state,
  isBusy,
  onChange,
  onConvert,
  onRun,
  onFreeform,
  onShowIssues,
  onRegister,
  registerBusy,
  onShowData,
}: {
  title: string;
  state: SourceSlotState;
  isBusy: boolean;
  onChange: (patch: Partial<SourceSlotState>) => void;
  onConvert: () => void;
  onRun: () => void;
  onFreeform: () => void;
  onRegister?: () => void;
  registerBusy?: boolean;
  onShowIssues: (severity: ImportIssueSeverity, messages: string[]) => void;
  onShowData: () => void;
}) {
  const converting =
    state.status === "uploading" || state.status === "converting";
  const convertedItems = countConversionItems(state.conversion);
  const convertedDays = countConversionDays(state.conversion);
  const convertedArrangements = countConversionArrangements(state.conversion);
  const reservationMatching = conversionReservationMatching(state.conversion);
  const llmReservationMatches = countLlmReservationMatches(state.conversion);
  const usage = formatUsage(state.conversion?.usage);
  const crmProjectId = state.crmProjectId ?? "";
  const label = state.label ?? "";
  const url = state.url ?? "";
  const sourceKind = state.sourceKind ?? "excel";
  const status = state.status ?? "idle";
  const isCrmSource = sourceKind === "crm-api";
  const isLlmConversion = shouldShowExternalConversionProgress(state);
  const canConvert = isCrmSource
    ? Boolean(crmProjectId.trim())
    : Boolean(state.file || url.trim());
  const warningMessages = extractConversionWarningMessages(state.conversion);

  return (
    <section className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm font-bold text-gray-900">{title}</div>
        <StatusPill status={status} />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-500">
            Source type
          </span>
          <select
            value={sourceKind}
            onChange={(event) =>
              onChange({
                sourceKind: event.target.value as ProjectDataSourceKind,
              })
            }
            disabled={isBusy}
            className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
          >
            {SOURCE_KIND_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-500">
            Label
          </span>
          <input
            value={label}
            onChange={(event) => onChange({ label: event.target.value })}
            disabled={isBusy}
            className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
          />
        </label>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {isCrmSource ? (
          <label key="crm-project-id" className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-500">
              <Database size={13} />
              CRM Project ID
            </span>
            <input
              value={crmProjectId}
              onChange={(event) =>
                onChange({ crmProjectId: event.target.value })
              }
              disabled={isBusy}
              inputMode="numeric"
              placeholder="1872"
              className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
            />
          </label>
        ) : (
          <label key={`${sourceKind}-file`} className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-500">
              <UploadCloud size={13} />
              File
            </span>
            <input
              type="file"
              accept={acceptsFor(sourceKind)}
              disabled={isBusy}
              onChange={(event) => {
                onChange({ file: event.target.files?.[0] ?? null });
              }}
              className="block w-full text-xs text-gray-600 file:mr-3 file:h-8 file:rounded-md file:border-0 file:bg-white file:px-3 file:text-xs file:font-semibold file:text-gray-700 hover:file:bg-gray-100 disabled:opacity-50"
            />
          </label>
        )}

        <label className="block">
          <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-500">
            <Link2 size={13} />
            URL
          </span>
          <input
            value={url}
            onChange={(event) => onChange({ url: event.target.value })}
            disabled={isBusy || isCrmSource}
            placeholder="https://..."
            className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
          />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onConvert}
          disabled={!canConvert || isBusy}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {converting ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <FileText size={14} />
          )}
          {isCrmSource ? "Load" : "Convert"}
        </button>
        {onRegister && !isCrmSource && (
          <button
            type="button"
            onClick={onRegister}
            disabled={!state.conversion?.conversionId || isBusy || registerBusy}
            title="変換した旅程を案件として登録します（チェックはまだ実行しません）。登録後は案件ページから何度でもチェックできます"
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {registerBusy ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <FileText size={14} />
            )}
            案件として登録
          </button>
        )}
        <button
          type="button"
          onClick={onRun}
          disabled={!state.conversion || isBusy}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Play size={14} />
          Run Check
        </button>
        <button
          type="button"
          onClick={onFreeform}
          disabled={!state.conversion || isBusy}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Play size={14} />
          Freeform Review
        </button>
        <button
          type="button"
          onClick={onShowData}
          disabled={!state.conversion || isBusy}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Database size={14} />
          View data
        </button>
        {state.file && (
          <span className="text-xs text-gray-500">
            {state.file.name} · {formatBytes(state.file.size)}
          </span>
        )}
      </div>

      {isLlmConversion && (
        <div className="mt-3">
          <RunProgress
            progress={buildExternalConversionProgress(title, state)}
            progressThresholdBytes={
              EXTERNAL_CONVERSION_PROGRESS_THRESHOLD_BYTES
            }
          />
        </div>
      )}

      {state.conversion && (
        <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-green-800">
            <CheckCircle2 size={15} />
            {convertedDays} days / {convertedItems} items
            {convertedArrangements > 0
              ? ` / ${convertedArrangements} arrangements`
              : ""}
            {typeof reservationMatching.llmMatches === "number"
              ? ` / ${llmReservationMatches} LLM matches`
              : ""}
          </div>
          <div className="mt-1 text-xs text-green-700">
            {state.conversion.source.label}
            {state.conversion.cached ? " · cached conversion" : ""}
            {usage ? ` · ${usage}` : ""}
          </div>
          {warningMessages.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-amber-700">
              <span>{warningMessages.length} conversion warnings</span>
              <button
                type="button"
                onClick={() => onShowIssues("warning", warningMessages)}
                className="rounded-md border border-amber-200 bg-white px-2 py-1 font-semibold text-amber-800 transition-colors hover:bg-amber-50"
              >
                View details
              </button>
            </div>
          )}
        </div>
      )}

      {state.error && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span className="min-w-0 flex-1">{state.error}</span>
          <button
            type="button"
            onClick={() => onShowIssues("error", [state.error as string])}
            className="shrink-0 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
          >
            Details
          </button>
        </div>
      )}
    </section>
  );
}

function ImportIssueDialog({
  issue,
  onClose,
}: {
  issue: ImportIssueDialogState | null;
  onClose: () => void;
}) {
  if (!issue) return null;

  const isError = issue.severity === "error";
  const title = isError ? "Import Error" : "Import Warnings";
  const tone = isError
    ? {
        icon: "text-red-600",
        heading: "text-red-900",
        badge: "border-red-200 bg-red-50 text-red-700",
        list: "border-red-100 bg-red-50/70 text-red-800",
      }
    : {
        icon: "text-amber-600",
        heading: "text-amber-900",
        badge: "border-amber-200 bg-amber-50 text-amber-700",
        list: "border-amber-100 bg-amber-50/70 text-amber-800",
      };

  return (
    <DialogShell
      onClose={onClose}
      ariaLabelledBy="import-issue-title"
      backdropAriaLabel="Close import issue dialog"
      panelClassName="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl"
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div className="min-w-0">
          <div className={`flex items-center gap-2 ${tone.heading}`}>
            <AlertTriangle size={18} className={tone.icon} />
            <h2 id="import-issue-title" className="text-base font-semibold">
              {title}
            </h2>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className={`rounded-md border px-2 py-0.5 ${tone.badge}`}>
              {issue.title}
            </span>
            <span className="min-w-0 truncate">{issue.sourceLabel}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          aria-label="Close import issue dialog"
        >
          <X size={18} />
        </button>
      </div>

      <div className="min-h-0 overflow-y-auto px-5 py-4">
        <ol className="space-y-2">
          {issue.messages.map((message, index) => (
            <li
              key={`${index}:${message}`}
              className={`rounded-md border px-3 py-2 text-sm leading-relaxed ${tone.list}`}
            >
              {message}
            </li>
          ))}
        </ol>
      </div>

      <div className="flex shrink-0 justify-end border-t border-gray-200 px-5 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
        >
          Close
        </button>
      </div>
    </DialogShell>
  );
}

function ImportDataDialog({
  data,
  onClose,
}: {
  data: ImportDataDialogState | null;
  onClose: () => void;
}) {
  const [activeTab, setActiveTab] = useState<ImportDataTab>("overview");
  const [exportStatus, setExportStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setActiveTab("overview");
    setExportStatus(null);
  }, [data]);

  const tabs = useMemo<
    Array<{ value: ImportDataTab; label: string; body: unknown }>
  >(
    () => [
      {
        value: "overview",
        label: "Overview",
        body: data?.conversion.projectData ?? null,
      },
      {
        value: "projectData",
        label: "Project JSON",
        body: data?.conversion.projectData ?? null,
      },
      {
        value: "conversionReport",
        label: "Conversion report",
        body: data?.conversion.conversionReport ?? null,
      },
      {
        value: "provenance",
        label: "Provenance",
        body: data?.conversion.provenance ?? null,
      },
    ],
    [data],
  );
  const activeBody = useMemo(
    () => tabs.find((tab) => tab.value === activeTab)?.body ?? null,
    [activeTab, tabs],
  );
  const summary = useMemo(
    () => summarizeImportDataBody(activeTab, activeBody),
    [activeBody, activeTab],
  );
  const preview = useMemo(
    () => stringifyImportDataPreview(activeBody),
    [activeBody],
  );
  const sourceLabel = data?.sourceLabel ?? "imported_data";
  const activeFileName = jsonFileName(sourceLabel, activeTab);
  const isOverviewTab = activeTab === "overview";

  const handleOpenJson = () => {
    setExportStatus(null);
    try {
      openJsonInNewWindow(activeBody);
    } catch (error) {
      setExportStatus(
        error instanceof Error ? error.message : "JSON could not be opened.",
      );
    }
  };

  const handleDownloadJson = () => {
    setExportStatus(null);
    try {
      downloadJsonValue(activeBody, activeFileName);
    } catch (error) {
      setExportStatus(
        error instanceof Error
          ? error.message
          : "JSON could not be downloaded.",
      );
    }
  };

  if (!data) return null;

  return (
    <DialogShell
      onClose={onClose}
      ariaLabelledBy="import-data-title"
      backdropAriaLabel="Close imported data dialog"
      panelClassName="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-7xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl"
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-gray-900">
            <Database size={18} className="text-blue-600" />
            <h2 id="import-data-title" className="text-base font-semibold">
              取り込んだ旅程データ
            </h2>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-700">
              {data.title}
            </span>
            <span className="min-w-0 truncate">{data.sourceLabel}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          aria-label="Close imported data dialog"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex shrink-0 gap-1 border-b border-gray-200 px-5 py-2">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => {
              setActiveTab(tab.value);
              setExportStatus(null);
            }}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
              activeTab === tab.value
                ? "bg-gray-900 text-white"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 overflow-y-auto bg-gray-50 p-4">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {summary.map((item) => (
            <div
              key={`${item.label}:${item.value}`}
              className="rounded-md border border-gray-200 bg-white px-3 py-2"
            >
              <div className="text-[11px] font-medium uppercase text-gray-400">
                {item.label}
              </div>
              <div className="mt-1 truncate text-sm font-semibold text-gray-800">
                {item.value}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs font-semibold text-gray-500">
            {isOverviewTab ? "Readable view" : "Preview"}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleOpenJson}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700"
            >
              <ExternalLink size={13} />
              {isOverviewTab ? "Open project JSON" : "Open full JSON"}
            </button>
            <button
              type="button"
              onClick={handleDownloadJson}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-blue-700"
            >
              <Download size={13} />
              {isOverviewTab ? "Download project JSON" : "Download JSON"}
            </button>
          </div>
        </div>

        {exportStatus && (
          <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
            {exportStatus}
          </div>
        )}

        {isOverviewTab ? (
          <div className="mt-2">
            <ProjectDataOverview
              projectData={data.conversion.projectData}
              conversionReport={data.conversion.conversionReport}
              sourceLabel={data.sourceLabel}
            />
          </div>
        ) : (
          <div className="mt-2 overflow-auto rounded-md bg-gray-950 p-4">
            <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-100">
              {preview}
            </pre>
          </div>
        )}
      </div>
    </DialogShell>
  );
}

function StatusPill({ status }: { status: SlotStatus }) {
  const map: Record<SlotStatus, { label: string; className: string }> = {
    idle: {
      label: "Idle",
      className: "border-gray-200 bg-white text-gray-500",
    },
    uploading: {
      label: "Uploading",
      className: "border-blue-200 bg-blue-50 text-blue-700",
    },
    converting: {
      label: "Converting",
      className: "border-blue-200 bg-blue-50 text-blue-700",
    },
    ready: {
      label: "Ready",
      className: "border-green-200 bg-green-50 text-green-700",
    },
    error: {
      label: "Error",
      className: "border-red-200 bg-red-50 text-red-700",
    },
  };
  const item = map[status];
  return (
    <span
      className={`inline-flex h-6 items-center rounded-md border px-2 text-xs font-semibold ${item.className}`}
    >
      {item.label}
    </span>
  );
}
